import React, { useEffect, useState, useRef } from "react";
import { TabMenu } from "primereact/tabmenu";
import Prenotazioni from "./Prenotazioni";
import Appuntamenti from "./Appuntamenti";
import Login from "./Login";
import Registration from "./Registration";
import RefertiOnline from "./RefertiOnline";
import Account from "./Account";
import "./MainPage.css";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../Redux/store";
import ConfigService from "../services/ConfigService";
import {
  setPatientInfo,
  setToken,
  resetUser,
} from "../Redux/reducer/userSlice";
import { popHistory, pushHistory } from "../Redux/reducer/historySlice";
import { Activity, GroupedActivity } from "../lib/types";
import { getFeatureFlags } from "../lib/config";
import { setGlobalActivities, setActivitiesLoading, resetGlobalData } from "../Redux/reducer/globalDataSlice";
import BookingService from "../services/api/BookingService";
import { mapActivitiesToUI } from "../lib/bookingMappers";
import logger from "../services/Logger";

const MainPage: React.FC = () => {
  // URL endpoint - costruiti dinamicamente da ConfigService
  const url_token = ConfigService.buildApiUrl("Auth/token");
  const url_patient_info = ConfigService.buildApiUrl("user/patient/info?codiceFiscale=");
  const url_getActivities = ConfigService.buildApiUrl("clientApi/getactivities");

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [activities, setActivities] = useState<Activity[]>([]);
  const [groupedActivities, setGroupedActivities] = useState<GroupedActivity[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [configLoaded, setConfigLoaded] = useState(false);
  const activitiesLoadAttempted = useRef(false); // Prevent multiple load attempts

  // Feature flags - lette dinamicamente da config.json
  const [featureFlags, setFeatureFlags] = useState({
    showPrenotazioni: false,
    showAppuntamenti: false,
    showRefertiOnline: false,
    showRegistrazione: false
  });
  const { showPrenotazioni, showAppuntamenti, showRefertiOnline, showRegistrazione } = featureFlags;

  // Attende che ConfigService abbia caricato la configurazione
  useEffect(() => {
    const loadFeatureFlags = async () => {
      try {
        console.log('🔧 Loading config...');
        await ConfigService.loadConfig();
        const flags = getFeatureFlags();
        console.log('✅ Feature flags loaded:', flags);
        logger.info("Feature flags loaded:", flags);
        setFeatureFlags(flags);
        setConfigLoaded(true);
      } catch (error) {
        console.error('❌ Failed to load feature flags:', error);
        logger.error("Failed to load feature flags:", error);
        setConfigLoaded(true); // Procedi comunque con i default
      }
    };
    loadFeatureFlags();
  }, []);

  const dispatch = useDispatch<AppDispatch>();
  const token = useSelector((state: RootState) => state.user.token);
  const isLoggedIn = useSelector((state: RootState) => state.user.isLoggedIn);
  const codiceFiscale = useSelector(
    (state: RootState) => state.user.codiceFiscale
  );
  const history = useSelector((state: RootState) => state.history.history);

  // Forza logout automatico all'accesso root (TEMPORANEAMENTE DISABILITATO PER DEBUG)
  /*
  useEffect(() => {
    // Se siamo sulla root e l'utente è loggato, forza il logout
    if (window.location.pathname === '/' && isLoggedIn) {
      logger.info('Auto-logout triggered on root access');
      const StorageService = require('../services/StorageService').default;
      StorageService.clear();
      window.location.reload();
    }
  }, []);
  */

  // Recupera activities e groupedActivities da Redux
  const globalActivities = useSelector((state: RootState) => state.globalData.globalActivities);
  const groupedActivitiesRedux = useSelector((state: RootState) => state.globalData.groupedActivities);
  const isActivitiesLoaded = useSelector((state: RootState) => state.globalData.isActivitiesLoaded);
  const activitiesLoadingRedux = useSelector((state: RootState) => state.globalData.activitiesLoading);

  // Usa activities da Redux se disponibili
  useEffect(() => {
    if (globalActivities.length > 0) {
      setActivities(globalActivities);
      setGroupedActivities(groupedActivitiesRedux);
      setLoading(false);
    }
  }, [globalActivities, groupedActivitiesRedux]);

  // Definisce dinamicamente gli elementi del menu
  const items = [
    // Prenotazioni è visibile anche senza login (per permettere ricerche a utenti non registrati)
    ...(showPrenotazioni
      ? [{ label: "Prenotazioni", icon: "pi pi-fw pi-calendar" }]
      : []),
    // Appuntamenti richiede login (sono appuntamenti personali)
    ...(showAppuntamenti && isLoggedIn
      ? [{ label: "Appuntamenti", icon: "pi pi-fw pi-list-check" }]
      : []),
    ...(showRefertiOnline && isLoggedIn
      ? [{ label: "Referti Online", icon: "pi pi-fw pi-file-pdf" }]
      : []),
    // Mostra Registrazione solo se non loggato E feature flag attivo
    ...(showRegistrazione && !isLoggedIn
      ? [{ label: "Registrazione", icon: "pi pi-fw pi-user-plus" }]
      : []),
    {
      label: isLoggedIn ? "Account" : "Login",
      icon: isLoggedIn ? "pi pi-fw pi-user" : "pi pi-fw pi-sign-in",
    },
    ...(isLoggedIn
      ? [
          {
            label: "Logout",
            icon: "pi pi-fw pi-sign-out",
            command: () => handleLogout(),
          },
        ]
      : []),
  ];

  // Calcola gli indici dei tab per passarli ai componenti
  const loginTabIndex = items.findIndex((item) => item.label === "Login");
  const registrationTabIndex = items.findIndex((item) => item.label === "Registrazione");

  // Funzione per gestire il logout
  const handleLogout = async () => {
    try {
      // Usa un timestamp per evitare loop tra tab multiple durante logout
      const logoutKey = "logout_in_progress";
      const logoutTimestamp = sessionStorage.getItem(logoutKey);
      const now = Date.now();

      // Se c'è già un logout in corso (negli ultimi 2 secondi), skip
      if (logoutTimestamp && (now - parseInt(logoutTimestamp)) < 2000) {
        logger.info("Logout già in corso da altra tab");
        return;
      }

      // Setta timestamp per evitare loop con altre tab
      sessionStorage.setItem(logoutKey, now.toString());

      // Reset dello stato Redux
      dispatch(resetUser());
      dispatch(resetGlobalData());

      // Pulizia storage
      const StorageService = require("../services/StorageService").default;
      StorageService.clear();

      // Rimuovi il flag dopo 1 secondo
      setTimeout(() => {
        sessionStorage.removeItem(logoutKey);
      }, 1000);

      // Reset dell'activeIndex per tornare alla schermata di login
      setActiveIndex(0);

      // Non fare reload, il componente si ri-renderizzerà automaticamente
      logger.info("Logout completato con successo");
    } catch (error) {
      logger.error("Errore durante il logout:", error);
      sessionStorage.removeItem("logout_in_progress");
    }
  };

  // Credenziali API da variabili d'ambiente
  const clientId = process.env.REACT_APP_CLIENT_ID;
  const tokenSecret = process.env.REACT_APP_CLIENT_SECRET;

  // Token client viene gestito in AuthService, non serve più qui
  /*
  useEffect(() => {
    const fetchToken = async () => {
      // Validazione variabili d'ambiente
      if (!clientId || !tokenSecret) {
        logger.error("ERRORE: Variabili d'ambiente CLIENT_ID o CLIENT_SECRET non configurate. Verifica il file .env");
        return;
      }

      try {
        const response = await fetch(url_token, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ClientId: clientId,
            ClientSecret: tokenSecret,
          }),
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        dispatch(setToken(data.accessToken));
      } catch (error) {
        logger.error("Fetch error:", error instanceof Error ? error.message : "Unexpected error");
      }
    };

    fetchToken();
  }, [dispatch, clientId, tokenSecret]);
  */

  useEffect(() => {
    const fetchPatientInfo = async () => {
      if (isLoggedIn && codiceFiscale) {
        try {
          const response = await fetch(`${url_patient_info}${codiceFiscale}`, {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
            },
          });

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          dispatch(setPatientInfo(data));
        } catch (error) {
          logger.error(
            "Fetch error:",
            error instanceof Error ? error.message : "Unexpected error"
          );
        }
      }
    };

    fetchPatientInfo();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, codiceFiscale, dispatch, token]);

  // Carica activities per booking (solo se necessario e non già caricate)
  useEffect(() => {
    const loadActivities = async () => {
      // Attendi che la configurazione sia caricata
      if (!configLoaded) {
        console.log("⏳ Waiting for config to load before fetching activities");
        return;
      }

      // Salta se booking non abilitato
      if (!showPrenotazioni && !showAppuntamenti) {
        console.log("⚠️ Booking features disabled, skipping activities load");
        setLoading(false);
        return;
      }

      // Se già abbiamo dati in Redux, usa quelli
      if (globalActivities.length > 0) {
        console.log("✅ Activities already in Redux:", globalActivities.length);
        setLoading(false);
        return;
      }

      // Usa ref per evitare chiamate multiple
      if (activitiesLoadAttempted.current) {
        console.log("⏳ Activities load already attempted, skipping");
        return;
      }

      // Segna che abbiamo tentato il caricamento
      activitiesLoadAttempted.current = true;

      try {
        dispatch(setActivitiesLoading(true));
        console.log("🚀 Loading activities for booking from API...");

        const backendActivities = await BookingService.getActivities();
        console.log("📦 Backend activities received:", backendActivities?.length || 0);
        console.log("📦 First 3 activities:", JSON.stringify(backendActivities?.slice(0, 3)));
        const mappedActivities = mapActivitiesToUI(backendActivities);
        console.log("📦 First 3 mapped:", JSON.stringify(mappedActivities?.slice(0, 3)));

        dispatch(setGlobalActivities(mappedActivities));
        console.log("✅ Activities stored in Redux:", mappedActivities.length);

      } catch (error) {
        console.error("❌ Failed to load activities:", error);
        logger.error("Failed to load activities", error);
        dispatch(setActivitiesLoading(false));
        setLoading(false);
        // Reset ref per permettere retry in caso di errore
        activitiesLoadAttempted.current = false;
      }
    };

    loadActivities();
  }, [configLoaded, showPrenotazioni, showAppuntamenti, globalActivities.length, dispatch]);

  const handleTabChange = (index: number) => {
    dispatch(pushHistory(activeIndex));
    setActiveIndex(index);
  };

  const handleBack = () => {
    if (history.length > 0) {
      const previousIndex = history[history.length - 1];
      dispatch(popHistory());
      setActiveIndex(previousIndex);
    }
  };

  // Mostra TabMenu se loggato OPPURE se showRegistrazione o showPrenotazioni è attivo
  const showTabMenu = isLoggedIn || showRegistrazione || showPrenotazioni;

  // Attendi che la configurazione sia caricata prima di mostrare il contenuto
  if (!configLoaded) {
    return (
      <div className="tabmenu-div" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <i className="pi pi-spin pi-spinner" style={{ fontSize: '2rem' }}></i>
      </div>
    );
  }

  return (
    <div className="tabmenu-div">
      {showTabMenu && (
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <TabMenu
            model={items}
            className="tabmenu"
            activeIndex={activeIndex}
            onTabChange={(e) => handleTabChange(e.index)}
            style={{ flex: 1 }}
          />
        </div>
      )}
      <i
        className="pi pi-arrow-left back-icon"
        onClick={handleBack}
        style={{ cursor: "pointer", margin: "10px", display: "none" }}
      ></i>
      <div className="content">
        {/* Prenotazioni è accessibile anche senza login per permettere ricerche */}
        {showPrenotazioni &&
          activeIndex ===
            items.findIndex((item) => item.label === "Prenotazioni") && (
            <Prenotazioni
              activities={activities}
              groupedActivities={groupedActivities}
              loading={loading}
            />
          )}
        {showAppuntamenti &&
          isLoggedIn &&
          activeIndex ===
            items.findIndex((item) => item.label === "Appuntamenti") && (
            <Appuntamenti />
          )}
        {showRefertiOnline &&
          isLoggedIn &&
          activeIndex ===
            items.findIndex((item) => item.label === "Referti Online") && (
            <RefertiOnline />
          )}
        {/* Mostra Registration se non loggato e siamo sul tab Registrazione */}
        {showRegistrazione &&
          !isLoggedIn &&
          activeIndex ===
            items.findIndex((item) => item.label === "Registrazione") && (
            <Registration
              setActiveIndex={setActiveIndex}
              loginTabIndex={loginTabIndex}
            />
          )}
        {/* Mostra Login se non loggato E siamo sul tab Login */}
        {!isLoggedIn &&
          activeIndex === items.findIndex((item) => item.label === "Login") && (
          <Login
            setActiveIndex={setActiveIndex}
            showRegistrazione={showRegistrazione}
            registrationTabIndex={registrationTabIndex}
          />
        )}
        {isLoggedIn &&
          activeIndex ===
            items.findIndex((item) => item.label === "Account") && <Account />}
      </div>
    </div>
  );
};

export default MainPage;
